import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

interface CacheEntry {
  response: HttpResponse<any>;
  expiry: number;
}

/**
 * Global HTTP Cache Interceptor.
 *
 * IMPORTANT: All API calls in this project use POST (even reads like
 * getAllProject, getAllProjectTasks, etc.). This interceptor therefore
 * caches POST responses based on a whitelist of "read" endpoint name
 * suffixes (the final path segment of the URL).
 *
 * Cache key = full URL + JSON-stringified request body, so different
 * query params / filters get their own cache bucket.
 *
 * On any POST to a "write" endpoint (create / update / delete / status),
 * all cache entries whose URL contains the same domain-keyword are cleared
 * (selective invalidation — not a full wipe).
 *
 * Registered as providedIn:'root' singleton so it can also be injected
 * directly for imperative control (e.g. cache.clear() on logout).
 * Use { provide: HTTP_INTERCEPTORS, useExisting: CacheInterceptor } in
 * AppModule to avoid a second instance.
 */
@Injectable({ providedIn: 'root' })
export class CacheInterceptor implements HttpInterceptor {
  private cache = new Map<string, CacheEntry>();

  /**
   * Groups of URL keywords.
   * - readSuffixes : POST endpoints that are safe to cache (list / get queries)
   * - writeSuffixes: POST endpoints that mutate data (create / update / delete)
   * - domainKey    : short string shared by all URLs in this group; used to
   *                  find and clear related cache entries on mutation.
   * - ttl          : milliseconds a cached response stays fresh.
   */
  private readonly groups: Array<{
    domainKey: string;
    readSuffixes: string[];
    writeSuffixes: string[];
    ttl: number;
  }> = [
    // ── Leads ──────────────────────────────────────────────────────────── 2 min
    {
      domainKey: 'Lead',
      readSuffixes: ['getLeads', 'getLeadRecord', 'getFilteredLeads', 'getLead', 'getLeadsField', 'getLeadAnalytics'],
      writeSuffixes: ['createLeads', 'updateLeads', 'deleteLeads', 'createLeadsField', 'updateLeadsField', 'deleteLeadsField', 'createBulkLead', 'updateBulkLead'],
      ttl: 2 * 60 * 1000,
    },

    // ── Projects ────────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'Project',
      readSuffixes: ['getAllProject', 'getProjectById', 'getProjects', 'getProjectModuleList'],
      writeSuffixes: ['createProject', 'updateProject', 'deleteProject'],
      ttl: 5 * 60 * 1000,
    },

    // ── Bugs ────────────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'Bug',
      readSuffixes: ['getAllBug', 'getBugById', 'getBugs', 'getBugActivity'],
      writeSuffixes: ['createBug', 'updateBug', 'deleteBug'],
      ttl: 5 * 60 * 1000,
    },

    // ── Project Tasks ───────────────────────────────────────────────────── 1 min
    {
      domainKey: 'ProjectTask',
      readSuffixes: ['getAllProjectTasks', 'getProjectTaskById'],
      writeSuffixes: ['createProjectTask', 'updateProjectTask', 'deleteProjectTask', 'updateProjectTaskStatus'],
      ttl: 60 * 1000,
    },

    // ── Task Items (subtasks) ────────────────────────────────────────────── 1 min
    {
      domainKey: 'TaskItem',
      readSuffixes: ['getAllTaskItems', 'getTaskItemById'],
      writeSuffixes: ['createTaskItem', 'updateTaskItem', 'deleteTaskItem', 'updateTaskItemStatus'],
      ttl: 60 * 1000,
    },

    // ── Task Comments ────────────────────────────────────────────────────── 1 min
    {
      domainKey: 'TaskComment',
      readSuffixes: ['getAllTaskComments', 'getTaskCommentById'],
      writeSuffixes: ['createTaskComment', 'updateTaskComment', 'deleteTaskComment'],
      ttl: 60 * 1000,
    },

    // ── Task Time Logs ───────────────────────────────────────────────────── 1 min
    {
      domainKey: 'TaskTimeLog',
      readSuffixes: ['getAllTaskTimeLogs', 'getTaskTimeLogById'],
      writeSuffixes: ['createTaskTimeLog', 'updateTaskTimeLog', 'deleteTaskTimeLog'],
      ttl: 60 * 1000,
    },

    // ── Task Activities ──────────────────────────────────────────────────── 2 min
    {
      domainKey: 'TaskActivity',
      readSuffixes: ['getAllTaskActivities'],
      writeSuffixes: [],
      ttl: 2 * 60 * 1000,
    },

    // ── Users / Departments / Centers / Roles / Teams ────────────────────── 5 min
    {
      domainKey: 'User',
      readSuffixes: ['getAllUsers', 'getAllUserData', 'getUser', 'getAllDepartment', 'getAllRoles', 'getAllCenter', 'getUserByDepartment', 'getAllTeam', 'getAllTeamMember'],
      writeSuffixes: ['createUser', 'updateUser', 'createDepartment', 'updateDepartment', 'deleteDepartment', 'createCenter', 'updateCenter', 'deleteCenter', 'createRole', 'updateRole', 'deleteRole', 'createTeam', 'updateTeam', 'deleteTeam', 'createTeamMember', 'deleteTeamMember'],
      ttl: 5 * 60 * 1000,
    },

    // ── Accounts ─────────────────────────────────────────────────────────── 3 min
    {
      domainKey: 'Account',
      readSuffixes: ['getAllAccount', 'getAccount', 'getAccountField'],
      writeSuffixes: ['createAccount', 'updateAccount', 'deleteAccount', 'createBulkAccount', 'createAccountField', 'updateAccountField', 'deleteAccountField'],
      ttl: 3 * 60 * 1000,
    },

    // ── Contacts ─────────────────────────────────────────────────────────── 3 min
    {
      domainKey: 'Contact',
      readSuffixes: ['getAllContact', 'getAllContactWithCount', 'getContactField'],
      writeSuffixes: ['createContact', 'updateContact', 'deleteContact', 'createBulkContact', 'createContactField', 'updateContactField', 'deleteContactField'],
      ttl: 3 * 60 * 1000,
    },

    // ── Deals ─────────────────────────────────────────────────────────────── 3 min
    {
      domainKey: 'Deal',
      readSuffixes: ['getAllDeal', 'getAllDealByUser'],
      writeSuffixes: ['createDeal', 'updateDeal', 'deleteDeal'],
      ttl: 3 * 60 * 1000,
    },

    // ── Quotes ────────────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'Quote',
      readSuffixes: ['getAllQuote'],
      writeSuffixes: ['createQuote', 'updateQuote', 'deleteQuote', 'sendQuote'],
      ttl: 5 * 60 * 1000,
    },

    // ── Invoices ──────────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'Invoice',
      readSuffixes: ['getAllInvoice'],
      writeSuffixes: ['createInvoice', 'updateInvoice', 'deleteInvoice', 'sendInvoice'],
      ttl: 5 * 60 * 1000,
    },

    // ── Tickets / Support ─────────────────────────────────────────────────── 2 min
    {
      domainKey: 'Ticket',
      readSuffixes: ['getAllTicket', 'getFilteredTickets', 'getAllTicketCommentById', 'getAllTicketHistory'],
      writeSuffixes: ['createTicket', 'updateTicket', 'deleteTicket', 'updateMultipleTicket', 'createTicketComment', 'updateTicketComment', 'createTicketHistory', 'createBulkTicketHis'],
      ttl: 2 * 60 * 1000,
    },

    // ── Events ────────────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'Event',
      readSuffixes: ['getAllEvent'],
      writeSuffixes: ['createEvent', 'updateEvent', 'deleteEvent'],
      ttl: 5 * 60 * 1000,
    },

    // ── Products ──────────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'Product',
      readSuffixes: ['getAllProduct'],
      writeSuffixes: ['createProduct', 'updateProduct', 'deleteProduct'],
      ttl: 5 * 60 * 1000,
    },

    // ── Categories ────────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'Category',
      readSuffixes: ['getAllCategory'],
      writeSuffixes: ['createCategory', 'updateCategory', 'deleteCategory'],
      ttl: 5 * 60 * 1000,
    },

    // ── Taxes ─────────────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'Tax',
      readSuffixes: ['getAllTaxe'],
      writeSuffixes: ['createTaxe', 'updateTaxe', 'deleteTaxe'],
      ttl: 5 * 60 * 1000,
    },

    // ── Pipeline ──────────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'Pipeline',
      readSuffixes: ['getAllPipeline'],
      writeSuffixes: ['createPipeline', 'updatePipeline', 'deletePipeline'],
      ttl: 5 * 60 * 1000,
    },

    // ── Sequences ─────────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'Sequence',
      readSuffixes: ['getAllSequence', 'getAllSequenceWithCount', 'getSequence', 'getAllSequenceTask', 'getAllSequenceTaskWithCount', 'getAllSequenceSchedule'],
      writeSuffixes: ['createSequence', 'updateSequence', 'deleteSequence', 'createSequenceTask', 'updateSequenceTask', 'deleteSequenceTask', 'createSequenceSchedule', 'updateSequenceSchedule'],
      ttl: 5 * 60 * 1000,
    },

    // ── Contact Groups ────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'ContactGroup',
      readSuffixes: ['getAllContact_group', 'getAllContactGroupList'],
      writeSuffixes: ['createContact_group', 'updateContact_group', 'deleteContact_group', 'createContactGroupList', 'updateContactGroupList', 'deleteContactGroupList'],
      ttl: 5 * 60 * 1000,
    },

    // ── User Logs ─────────────────────────────────────────────────────────── 3 min
    {
      domainKey: 'UserLog',
      readSuffixes: ['getAllUserLog'],
      writeSuffixes: ['createUserLog', 'updateUserLog', 'deleteUserLog'],
      ttl: 3 * 60 * 1000,
    },

    // ── Lead Spaces ───────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'LeadSpace',
      readSuffixes: ['getAllLeadSpace'],
      writeSuffixes: ['createLeadSpace', 'updateLeadSpace', 'deleteLeadSpace'],
      ttl: 5 * 60 * 1000,
    },

    // ── Forecast ──────────────────────────────────────────────────────────── 5 min
    {
      domainKey: 'Forecast',
      readSuffixes: ['getAllForecast'],
      writeSuffixes: ['createForecast', 'updateForecast', 'deleteForecast'],
      ttl: 5 * 60 * 1000,
    },
  ];

  /**
   * URL substrings that must NEVER be cached regardless of suffix match.
   */
  private readonly neverCache = [
    'login', 'logout', 'sendEmail', 'sendWhatsapp', 'broadcast',
    'report', 'analytics', 'upload', 'verify', 'onboard',
  ];

  // ─────────────────────────────────────────────────────────────────────────
  // HttpInterceptor
  // ─────────────────────────────────────────────────────────────────────────

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // 1. Manual bypass via header
    if (request.headers.get('skip-cache') === 'true') {
      return next.handle(request);
    }

    // 2. Hard-excluded URLs (auth, email sending, etc.)
    if (this.neverCache.some((kw) => request.url.toLowerCase().includes(kw))) {
      return next.handle(request);
    }

    // 3. Only intercept POST (all reads and writes in this project use POST)
    if (request.method !== 'POST') {
      return next.handle(request);
    }

    // 4. Identify which group this URL belongs to
    const urlLower = request.url.toLowerCase();
    const endpointName = this.getEndpointName(request.url);

    const group = this.groups.find(
      (g) =>
        g.readSuffixes.some((s) => s.toLowerCase() === endpointName) ||
        g.writeSuffixes.some((s) => s.toLowerCase() === endpointName),
    );

    if (!group) {
      return next.handle(request); // unknown endpoint — pass through
    }

    // 5. Is this a write (mutation) endpoint?
    const isMutation = group.writeSuffixes.some(
      (s) => s.toLowerCase() === endpointName,
    );

    if (isMutation) {
      this.clearGroupCache(group.domainKey);
      return next.handle(request);
    }

    // 6. Read endpoint — build cache key from URL + body
    const cacheKey = this.buildKey(request);
    const cached = this.cache.get(cacheKey);

    if (cached && cached.expiry > Date.now()) {
      console.log('[Cache] HIT:', endpointName);
      return of(cached.response.clone());
    }

    if (cached) {
      this.cache.delete(cacheKey);
    }

    // 7. Fetch and store
    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.status === 200) {
          console.log('[Cache] MISS → stored:', endpointName);
          this.cache.set(cacheKey, {
            response: event.clone(),
            expiry: Date.now() + group.ttl,
          });
        }
      }),
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────────────────

  /** Clear the entire cache. Call on logout. */
  clear(): void {
    this.cache.clear();
    console.log('[Cache] CLEARED ALL');
  }

  /** Manually remove entries matching a URL keyword. */
  invalidate(keyword: string): void {
    let count = 0;
    for (const [key] of this.cache) {
      if (key.includes(keyword)) {
        this.cache.delete(key);
        count++;
      }
    }
    if (count > 0) {
      console.log(`[Cache] INVALIDATED ${count} entries matching: ${keyword}`);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private helpers
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Extract the last path segment of the URL (the Lambda function name).
   * e.g. "https://xxx.amazonaws.com/dev/getAllProjectTasks" → "getAllProjectTasks"
   */
  private getEndpointName(url: string): string {
    try {
      const path = new URL(url).pathname;
      const segments = path.split('/').filter(Boolean);
      return segments[segments.length - 1]?.toLowerCase() ?? '';
    } catch {
      // Fallback for relative URLs
      const parts = url.split('/').filter(Boolean);
      return parts[parts.length - 1]?.toLowerCase() ?? '';
    }
  }

  /**
   * Cache key = URL + serialised request body.
   * This ensures paginated / filtered calls each get their own bucket.
   */
  private buildKey(request: HttpRequest<any>): string {
    let body = '';
    try {
      body = JSON.stringify(request.body ?? {});
    } catch {
      body = String(request.body);
    }
    return `${request.url}::${body}`;
  }

  /**
   * Clear all cache entries that belong to a given domain group.
   * We reconstruct which keys are "owned" by the group using its readSuffixes.
   */
  private clearGroupCache(domainKey: string): void {
    const group = this.groups.find((g) => g.domainKey === domainKey);
    if (!group) {
      this.cache.clear();
      return;
    }

    const suffixSet = new Set([
      ...group.readSuffixes.map((s) => s.toLowerCase()),
      ...group.writeSuffixes.map((s) => s.toLowerCase()),
    ]);

    let cleared = 0;
    for (const [key] of this.cache) {
      // key format: "https://.../{endpointName}::{body}"
      const urlPart = key.split('::')[0];
      const name = this.getEndpointName(urlPart);
      if (suffixSet.has(name)) {
        this.cache.delete(key);
        cleared++;
      }
    }

    if (cleared > 0) {
      console.log(`[Cache] SELECTIVE CLEAR — ${cleared} entries for domain: ${domainKey}`);
    }
  }
}
